import React, { Component } from 'react';
import AWSAuth from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';

import { RouteComponentProps, withRouter } from 'react-router';

import { AuthState, Channel, ICognitoUser, PayloadEvent } from 'types/aws-amplify';
import { getAuthStateFromPath } from 'helpers';

import AuthContext from './Context';
import { IWithAuthData } from './types';
import {
  FORGOT_PASSWORD,
  PASSWORD_RESET_REQUIRED_EXCEPTION,
  USER_NOT_AUTHENTICATED,
  USER_NOT_CONFIRMED_EXCEPTION,
  ES_PATH_AND_QUERY,
} from 'features/auth/constants';

class Provider extends Component<RouteComponentProps, IWithAuthData> {
  state: IWithAuthData = {
    id: '',
    state: AuthState.loading,
    user: null,
  };

  constructor(props: RouteComponentProps) {
    super(props);
    Hub.listen(Channel.auth, this.onHubCapsule);
  }

  componentDidMount() {
    this.checkUser();
  }

  update = (data: Partial<IWithAuthData>) => {
    this.setState({ ...this.state, ...data });
  };

  checkUser() {
    AWSAuth.currentAuthenticatedUser()
      .then(this.onSuccess)
      .catch(this.onError);
  }

  onSuccess = (user: ICognitoUser) => {
    const idToken = user.signInUserSession.idToken.payload;
    const userId = idToken['custom:user_profile_id'] || idToken.sub;
    this.setState({ state: AuthState.signedIn, id: userId });
  };

  onError = (reason: string) => {
    const { location } = this.props;
    const { pathname } = location;

    if (reason === USER_NOT_AUTHENTICATED) {
      const params = new URLSearchParams(window.location.search);
      // SSO Allowed user in, but account is disabled
      if (params.get('code') && params.get('state')) {
        this.setState({ state: AuthState.suspended });
        return;
      }
    }

    const state = getAuthStateFromPath(pathname);
    this.setState({ state });
  };

  onHubCapsule = (capsule: any) => {
    const { channel, payload } = capsule;
    const { event, data } = payload;

    const isAuthChannel = channel === Channel.auth;
    const needsConfirmed =
      (event === PayloadEvent.signUp && !data.userConfirmed) ||
      (event === PayloadEvent.signIn_failure && data.code === USER_NOT_CONFIRMED_EXCEPTION);

    if (needsConfirmed) {
      return;
    }

    const passwordResetRequired =
      event === PayloadEvent.signIn_failure && data.code === PASSWORD_RESET_REQUIRED_EXCEPTION;
    if (passwordResetRequired) {
      return;
    }

    if (event === FORGOT_PASSWORD) {
      return;
    }

    if (event === PayloadEvent.signIn) {
      const pathAndQuery = sessionStorage.getItem(ES_PATH_AND_QUERY);
      if (pathAndQuery) {
        sessionStorage.removeItem(ES_PATH_AND_QUERY);
        location.assign(`${location.protocol}//${location.hostname}${pathAndQuery}`);
      }
    }

    if (isAuthChannel) {
      this.checkUser();
    }
  };

  render() {
    const auth = { data: this.state, update: this.update };

    return <AuthContext.Provider value={{ auth }}>{this.props.children}</AuthContext.Provider>;
  }
}

export default withRouter(Provider);
